import { PoolDropzone, PoolLinearProgress, PoolTypography } from '@synergy/components';
import { Theme } from '@mui/material';
import { downloadUrl } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import urljoin from 'url-join';

import * as CustomerDetailDocumentsActions from './store/customer-detail-documents-actions';
import * as CustomerDetailDocumentsSelectors from './store/customer-detail-documents-selectors';
import { CustomerDetailDocumentsEditTitleModal } from './components/customer-detail-documents-edit-title-modal';
import { CustomerDocument } from './models/customer-detail-document';
import { DocumentsTable } from '../../../components/documents/documents-table';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    progress: {
        marginTop: theme.spacing(2),
    },
    progressPlaceholder: {
        height: 4,
    },
    dropzone: {
        height: 160,
    },
    sectionContainer: {
        marginTop: theme.spacing(6),
    },
}));

export const CustomerDetailDocuments = () => {
    const dispatch = useThunkDispatch();
    const { id: customerIdParam } = useParams<{ id: string }>();
    const classes = useStyles();
    const [selectedDocument, setSelectedDocument] = useState<CustomerDocument | null>(null);

    const documents = useAppSelector(CustomerDetailDocumentsSelectors.documentsSelector);
    const progress = useAppSelector(CustomerDetailDocumentsSelectors.overallUploadProgressSelector);
    const documentBaseUrl = useAppSelector(state => state.settings.privateFilesystemPath);

    useEffect(() => {
        if (customerIdParam && +customerIdParam) {
            dispatch(CustomerDetailDocumentsActions.fetchCustomerDocuments(+customerIdParam));
        }
    }, [customerIdParam, dispatch]);

    const handleEditTitleStart = (document: CustomerDocument) => {
        setSelectedDocument(document);
    };

    const handleEditTitleComplete = (name: string) => {
        if (!selectedDocument) {
            return;
        }
        dispatch(CustomerDetailDocumentsActions.renameDocument(selectedDocument.id, name));
        setSelectedDocument(null);
    };

    const handleDownload = (url: string, documentname: string) => {
        downloadUrl(urljoin(documentBaseUrl, url), documentname);
    };

    const handleDelete = (document: CustomerDocument) => {
        dispatch(CustomerDetailDocumentsActions.deleteDocument(document.id));
    };

    const handleDrop = (acceptedFiles: File[]) => {
        const deduplicatedDocuments = acceptedFiles.filter(
            file => !documents.some(doc => doc.originalName === file.name && doc.size === file.size)
        );
        if (deduplicatedDocuments.length > 0) {
            dispatch(CustomerDetailDocumentsActions.addCustomerDocuments(deduplicatedDocuments));
        }
    };

    return (
        <div>
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.upload" />
            <PoolDropzone onDrop={handleDrop} className={classes.dropzone} />
            {progress ? (
                <PoolLinearProgress variant="determinate" value={progress} className={classes.progress} />
            ) : (
                <div className={`${classes.progress} ${classes.progressPlaceholder}`} />
            )}
            {documents && documents.length > 0 && (
                <div className={classes.sectionContainer}>
                    <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.files" />
                    <DocumentsTable
                        documents={documents}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEditTitle={handleEditTitleStart}
                    />
                </div>
            )}
            <CustomerDetailDocumentsEditTitleModal
                document={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onConfirm={handleEditTitleComplete}
            />
        </div>
    );
};
